import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { site } from "@/mocks/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomeTop = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const solid = scrolled || !isHomeTop || menuOpen;

  const goTo = (to: string) => {
    const [path, hash] = to.split("#");
    if (hash) {
      if (location.pathname !== path) {
        navigate(path);
        window.setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 120);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }
    navigate(path);
  };

  const handleNav = (event: React.MouseEvent, to: string) => {
    if (to.includes("#")) {
      event.preventDefault();
      goTo(to);
      setMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "bg-foreground-950/95 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-shell items-center justify-between px-4 md:h-[72px] md:px-8">
        <Link
          to="/"
          className="group flex items-center gap-2.5"
          aria-label="爆点观察局首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-background-50">
              {site.name}
            </span>
            <span className="mt-0.5 hidden text-[10px] tracking-[0.2em] text-background-300 sm:block">
              HOT TOPIC LAB
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {site.nav.map((item) => {
            const isHash = item.to.includes("#");
            return (
              <NavLink
                key={item.to}
                to={item.to.split("#")[0] || "/"}
                onClick={(event) => handleNav(event, item.to)}
                className={({ isActive }) =>
                  `relative rounded-md px-3.5 py-2 text-sm transition-colors ${
                    !isHash && isActive
                      ? "text-accent-400"
                      : "text-background-200 hover:text-background-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <span className="flex items-center gap-2 rounded-full border border-background-50/20 px-3 py-1.5 text-xs text-background-200">
            <span className="relative flex h-2 w-2 items-center justify-center">
              <span className="live-dot h-2 w-2 rounded-full bg-accent-500" />
            </span>
            实时更新中
          </span>
          <button
            type="button"
            onClick={() => goTo("/#subscribe")}
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            订阅热点
          </button>
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-md text-background-50 md:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-3-line text-2xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-50/10 bg-foreground-950 px-4 pb-5 pt-3 md:hidden">
          <nav className="flex flex-col" aria-label="移动端导航">
            {site.nav.map((item) => (
              <Link
                key={item.to}
                to={item.to.split("#")[0] || "/"}
                onClick={(event) => handleNav(event, item.to)}
                className="flex items-center justify-between border-b border-background-50/10 py-3 text-sm text-background-100"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-background-400" />
              </Link>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              goTo("/#subscribe");
            }}
            className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
          >
            订阅热点更新
          </button>
        </div>
      )}
    </header>
  );
}